import React, { FunctionComponent, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  LinearProgress,
  Theme,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { DropzoneArea } from "material-ui-dropzone";
import { ImageInformation } from "../../models/ImageInformation";
import { Alert } from "@material-ui/lab";
import { useI18n } from "../../hooks/useI18n";

const useStyles = makeStyles((theme: Theme) => ({
  section: {
    // margin: theme.spacing(2),

    [theme.breakpoints.up("sm")]: {
      marginLeft: theme.spacing(0),
      marginRight: theme.spacing(0)
    }
  },
  image: {
    width: "100%",
    height: "100%",
    minHeight: "150px",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center center",
    backgroundSize: "contain",
    marginBottom: theme.spacing(2)
  },
  spinner: {
    width: "100%",
    height: "100%",
    minHeight: "150px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  area: {
    marginBottom: theme.spacing(2)
  }
}));

interface ImageEditorProps {
  title?: string;
  acceptedFiles?: string[];
  loading?: boolean;
  initial: ImageInformation;
  onChange: (data: ImageInformation) => void;
  helpText?: string;
  dropZoneClass?: string;
  dropZoneTextClass?: string;
  variant?: any;
}

export const ImageEditor: FunctionComponent<ImageEditorProps> = ({
  title,
  acceptedFiles,
  initial,
  loading,
  onChange,
  helpText,
  dropZoneClass,
  dropZoneTextClass,
  variant
}) => {
  const classes = useStyles();
  const { t } = useI18n();
  const [state, setState] = useState<ImageInformation>(initial);
  const [showDrop, setShowDrop] = useState(
    initial.url === "" || initial.url === undefined
  );

  useEffect(() => {
    if (loading !== undefined) {
      setState(initial);
    }
  }, [loading]);

  return (
    <Card className={classes.section} variant={variant ?? "outlined"}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignSelf: "flex-start",
            width: "100%"
          }}
        >
          <Typography gutterBottom variant="h6" component="h6">
            {title ? title : t("common.image")}
          </Typography>
          <Box sx={{ flex: "1 1 auto" }} />
          {state.url && (
            <Button color={"primary"} onClick={() => setShowDrop(!showDrop)}>
              {showDrop ? t("common.cancel") : t("common.uploadNew")}
            </Button>
          )}
        </Box>

        {loading && (
          <div className={classes.spinner}>
            <CircularProgress size={40} />
          </div>
        )}
        {!loading && !showDrop && state.url && (
          <div
            className={classes.image}
            style={{ backgroundImage: "url(" + state.url + ")" }}
          />
        )}

        {showDrop && (
          <div className={classes.area}>
            <DropzoneArea
              dropzoneClass={dropZoneClass ?? ""}
              dropzoneParagraphClass={dropZoneTextClass ?? ""}
              acceptedFiles={acceptedFiles ? acceptedFiles : ["image/*"]}
              showPreviewsInDropzone={false}
              onChange={files => {
                if (files.length === 0) {
                  return;
                }

                const file = files[files.length - 1];
                const reader = new FileReader();

                reader.onload = function () {
                  const url = reader.result as string;
                  const blob = files[files.length - 1];

                  setState({ url, blob });
                  setShowDrop(false);
                  onChange({ url, blob });
                };

                reader.readAsDataURL(file);
              }}
            />
          </div>
        )}
        {helpText !== undefined && <Alert severity={"info"}>{helpText}</Alert>}
      </CardContent>
    </Card>
  );
};
